import React, { Fragment } from 'react'
import { ComponentType, memo, MouseEvent, useCallback, useContext, useMemo, useState } from 'react'
import { CirclePlusMinor, InviteMinor, LinkMinor, SaveMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { ActionButton } from 'src/components/shared/ActionButton'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { BasicModal } from 'src/components/shared/modal/BasicModal'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { MenuActionItem } from 'src/interfaces/ui'
import { InviteWorkersViaCSVForm } from './invite-workers-via-csv-form/InviteWorkersViaCSVForm'
import { InviteWorkersViaLinkForm } from './invite-workers-via-link-form/InviteWorkersViaLinkForm'
import { InviteWorkersViaMessageForm } from './invite-workers-via-message-form/InviteWorkersViaMessageForm'
import { InviteWorkersModalType } from './enums'

type ModalComponent = ComponentType<{ onClose: () => void; onSubmit?: () => void }>

export const INVITE_WORKERS_MODAL_CONFIG = {
  [InviteWorkersModalType.InviteViaMessage]: InviteWorkersViaMessageForm,
  [InviteWorkersModalType.InviteViaLink]: InviteWorkersViaLinkForm,
  [InviteWorkersModalType.UploadCsv]: InviteWorkersViaCSVForm,
}

const InviteWorkersButtonComponent = () => {
  const { reload } = useContext(ReloadWrapperContext)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [activeModal, setActiveModal] = useState<InviteWorkersModalType | null>(null)

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => setAnchorEl(null), [])
  const handleCloseModal = useCallback(() => setActiveModal(null), [])

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    return [
      {
        label: 'Invite contractor via message',
        icon: <InviteMinor width={20} />,
        dataTestId: 'contractors-onboarding-invite-via-message',
        onClick: () => {
          setActiveModal(InviteWorkersModalType.InviteViaMessage)
          setAnchorEl(null)
        },
      },
      {
        label: 'Invite contractor via link',
        icon: <LinkMinor width={20} />,
        dataTestId: 'contractors-onboarding-invite-via-link',
        onClick: () => {
          setActiveModal(InviteWorkersModalType.InviteViaLink)
          setAnchorEl(null)
        },
      },
      {
        label: 'Upload CSV',
        icon: <SaveMinor width={20} />,
        dataTestId: 'contractors-onboarding-upload-csv',
        onClick: () => {
          setActiveModal(InviteWorkersModalType.UploadCsv)
          setAnchorEl(null)
        },
      },
    ]
  }, [])

  const ActiveModalComponent = activeModal
    ? (INVITE_WORKERS_MODAL_CONFIG[activeModal] as ModalComponent)
    : null

  const isMenuOpen = Boolean(anchorEl)
  const isModalOpen = activeModal && ActiveModalComponent

  return (
    <Fragment>
      <ActionButton
        text="Invite contractors"
        selected={!!anchorEl}
        className={tw`px-4`}
        color="dark"
        icon={<CirclePlusMinor fill="white" width={20} />}
        onClick={handleClick}
        data-testid="contractors-onboarding-invite-menu-btn"
      />
      <ActionMenu
        anchorEl={anchorEl}
        items={actionItems}
        isOpen={isMenuOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        itemClassName={tw`w-[232px]`}
        onClose={handleCloseMenu}
      />
      {isModalOpen && (
        <BasicModal isOpen={true} onClose={handleCloseModal}>
          <ActiveModalComponent
            onSubmit={activeModal !== InviteWorkersModalType.InviteViaLink ? reload : undefined}
            onClose={handleCloseModal}
          />
        </BasicModal>
      )}
    </Fragment>
  )
}

export const InviteWorkersButton = memo(InviteWorkersButtonComponent)
